"use client";

import React, { useState, useRef, useEffect } from 'react';
import { Volume2, Pause, Play } from 'lucide-react';
import Image from 'next/image';

interface AudioPlayerProps {
  src?: string;
  initialDuration?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek?: (time: number) => void;
  className?: string;
}

export default function AudioPlayer({
  src,
  initialDuration = 0,
  onPlay,
  onPause,
  onSeek,
  className = ''
}: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(null);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(Math.max(0, seconds) / 60);
    const secs = Math.floor(Math.max(0, seconds) % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

// Play && Pause
  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      onPause?.();
    } else {
      audioRef.current?.play()
        .then(() => {
          onPlay?.();
          updateProgress(); // Start progress updates
        })
        .catch(error => {
          console.error('Error playing audio:', error);
        });
    }
    setIsPlaying(!isPlaying);
  };



  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const newTime = percentage * (audioRef.current.duration || duration);
    
    // Update the audio's current time
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    // If audio was paused, update the play state
    if (!isPlaying && newTime < (audioRef.current.duration || duration)) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          onPlay?.();
          updateProgress(); // Restart progress updates
        });
    }
    
    onSeek?.(newTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      setDuration(audioDuration);
      
      // Log for debugging
      
      // If this is the first time loading, set initial time
      if (currentTime === 0 && audioDuration > 0) {
        setCurrentTime(0);
      }
    }
  };
  
  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Update progress bar animation
  const updateProgress = () => {
    if (!audioRef.current) return;
    
    const currentTime = audioRef.current.currentTime;
    const duration = audioRef.current.duration || 1; // Avoid division by zero
    
    setCurrentTime(currentTime);
    
    if (currentTime >= duration) {
      setIsPlaying(false);
      onPause?.();
    } else if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  };

  // Handle time updates from audio element
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    onPause?.();
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full p-4">
      <h3 className="text-14-english-600 text-020202 mb-4">Listen to this article</h3>
      
      <div className="flex items-center gap-2 bg-ECEEF3 border border-DCDCDC px-3 py-2">
        {/* Volume Icon */}
        <button className="text-012061 hover:text-012061 transition-colors flex-shrink-0">
          <Volume2 size={20} />
        </button>
        
        {/* Play/Pause Button */}
        <button 
          onClick={togglePlay}
          className="w-8 h-8 rounded-full text-012061 bg-DCDCDC flex items-center justify-center transition-colors flex-shrink-0"
        >
          {isPlaying ? 
            <Pause size={16} fill="" /> : 
            <Play size={16} fill="" className="ml-0.5" />
          }
        </button>
        
        {/* Progress Bar */}
        <div className="flex-1 flex items-center gap-3">
          <div 
            ref={progressRef}
            onClick={handleProgressClick}
            className="flex-1 h-1 bg-gray-300 rounded-full cursor-pointer relative group"
          >
            <div 
              className="absolute top-0 left-0 h-full bg-012061 rounded-full transition-all"
              style={{ width: `${progressPercentage}%` }}
            />
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-012061 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progressPercentage}% - 6px)` }}
            />
          </div>
          
          {/* Duration */}
          <span className="text-sm text-1B1B1B font-medium w-10 text-right flex-shrink-0">
            -{formatTime(duration - currentTime)}
          </span>
        </div>
      </div>

      {/* Audio element (hidden) */}
      <audio
        ref={audioRef}
        src={"/dinamani/monica.mp3"}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        className="hidden"
      />
    </div>  
  );
}